// src/pat-parser.ts
function parsePATFile(content) {
  try {
    const lines = content.split("\n").map((l) => l.trim()).filter((l) => l && !l.startsWith(";"));
    const patterns = [];
    let currentPattern = null;
    for (const line of lines) {
      if (line.startsWith("*")) {
        if (currentPattern) {
          patterns.push(currentPattern);
        }
        const parts = line.substring(1).split(",");
        currentPattern = {
          name: parts[0].trim(),
          description: parts[1] ? parts[1].trim() : "",
          lines: []
        };
      } else if (currentPattern && line.match(/^-?\d/)) {
        const parts = line.split(",").map((p) => {
          const trimmed = p.trim();
          const parsed = parseFloat(trimmed);
          return isNaN(parsed) ? 0 : parsed;
        });
        if (parts.length >= 5 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2]) && !isNaN(parts[3]) && !isNaN(parts[4])) {
          currentPattern.lines.push({
            angle: parts[0],
            xOrigin: parts[1],
            yOrigin: parts[2],
            xOffset: parts[3],
            yOffset: parts[4],
            dashes: parts.slice(5).filter((d) => !isNaN(d))
          });
        }
      }
    }
    if (currentPattern) {
      patterns.push(currentPattern);
    }
    if (patterns.length === 0) {
      throw new Error("No valid patterns found in file");
    }
    return patterns;
  } catch (err) {
    throw new Error(`Parse error: ${err instanceof Error ? err.message : "Unknown error"}`);
  }
}

// src/pattern-renderer.ts
import * as THREE from "three";
function clipLineSegment(x1, y1, x2, y2, clipBounds) {
  const minBound = -clipBounds;
  const maxBound = clipBounds;
  const INSIDE = 0;
  const LEFT = 1;
  const RIGHT = 2;
  const BOTTOM = 4;
  const TOP = 8;
  const computeOutCode = (x, y) => {
    let code = INSIDE;
    if (x < minBound) code |= LEFT;
    else if (x > maxBound) code |= RIGHT;
    if (y < minBound) code |= BOTTOM;
    else if (y > maxBound) code |= TOP;
    return code;
  };
  let outcode1 = computeOutCode(x1, y1);
  let outcode2 = computeOutCode(x2, y2);
  while (true) {
    if (!(outcode1 | outcode2)) {
      return { x1, y1, x2, y2 };
    } else if (outcode1 & outcode2) {
      return null;
    } else {
      const outcodeOut = outcode1 ? outcode1 : outcode2;
      let x = 0, y = 0;
      if (outcodeOut & TOP) {
        x = x1 + (x2 - x1) * (maxBound - y1) / (y2 - y1);
        y = maxBound;
      } else if (outcodeOut & BOTTOM) {
        x = x1 + (x2 - x1) * (minBound - y1) / (y2 - y1);
        y = minBound;
      } else if (outcodeOut & RIGHT) {
        y = y1 + (y2 - y1) * (maxBound - x1) / (x2 - x1);
        x = maxBound;
      } else if (outcodeOut & LEFT) {
        y = y1 + (y2 - y1) * (minBound - x1) / (x2 - x1);
        x = minBound;
      }
      if (outcodeOut === outcode1) {
        x1 = x;
        y1 = y;
        outcode1 = computeOutCode(x1, y1);
      } else {
        x2 = x;
        y2 = y;
        outcode2 = computeOutCode(x2, y2);
      }
    }
  }
}
function renderPatternToSceneOptimized(scene, pattern, options = {}) {
  const { patternScale = 1, rotation = 0, color = 0, layerType, clipBounds = 300 } = options;
  const colorValue = typeof color === "string" ? parseInt(color.replace("#", ""), 16) : color;
  const material = new THREE.LineBasicMaterial({
    color: colorValue,
    linewidth: 2
  });
  const toRemove = scene.children.filter((child) => {
    if (child.userData.isPattern) {
      if (layerType) {
        return child.userData.layerType === layerType;
      }
      return true;
    }
    return false;
  });
  toRemove.forEach((obj) => {
    if (obj instanceof THREE.LineSegments || obj instanceof THREE.Line) {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach((mat) => {
            if (mat && typeof mat.dispose === "function") mat.dispose();
          });
        } else if (typeof obj.material.dispose === "function") {
          obj.material.dispose();
        }
      }
      scene.remove(obj);
    }
  });
  const allPoints = [];
  const MAX_POINTS = 2e6;
  const drawStep = 2;
  const maxDist = clipBounds + 50;
  pattern.lines.forEach((lineData) => {
    const angleRad = (lineData.angle + rotation) * Math.PI / 180;
    const dirX = Math.cos(angleRad);
    const dirY = Math.sin(angleRad);
    const perpX = -dirY;
    const perpY = dirX;
    const lineOriginX = lineData.xOrigin * patternScale;
    const lineOriginY = lineData.yOrigin * patternScale;
    const deltaX = lineData.xOffset * patternScale;
    const deltaY = lineData.yOffset * patternScale;
    const perpDist = deltaX * perpX + deltaY * perpY;
    let actualPerpDist = Math.abs(perpDist) < 1e-3 ? Math.sqrt(deltaX ** 2 + deltaY ** 2) : Math.abs(perpDist);
    if (actualPerpDist === 0) actualPerpDist = 10 * patternScale;
    const numLines = Math.min(Math.ceil(clipBounds / actualPerpDist) + 5, 500);
    const patternLength = lineData.dashes.reduce((sum, d) => sum + Math.abs(d), 0) * patternScale;
    for (let lineNum = -numLines; lineNum <= numLines; lineNum++) {
      if (allPoints.length >= MAX_POINTS) break;
      const parallelOriginX = lineOriginX + perpX * actualPerpDist * lineNum;
      const parallelOriginY = lineOriginY + perpY * actualPerpDist * lineNum;
      const dashOffset = (deltaX * dirX + deltaY * dirY) * lineNum;
      const adjustedMaxDist = maxDist + Math.abs(dashOffset);
      for (let dist = -adjustedMaxDist; dist < adjustedMaxDist; dist += drawStep) {
        let isInDrawPhase = true;
        if (patternLength > 0) {
          const relativeDist = dist - dashOffset;
          const patternPos = (relativeDist % patternLength + patternLength) % patternLength;
          let accumulated = 0;
          for (const dash of lineData.dashes) {
            const dashLen = Math.abs(dash) * patternScale;
            accumulated += dashLen;
            if (patternPos < accumulated) {
              isInDrawPhase = dash > 0;
              break;
            }
          }
        }
        if (isInDrawPhase) {
          const x1 = parallelOriginX + dirX * dist;
          const y1 = parallelOriginY + dirY * dist;
          const x2 = parallelOriginX + dirX * (dist + drawStep);
          const y2 = parallelOriginY + dirY * (dist + drawStep);
          const clipped = clipLineSegment(x1, y1, x2, y2, clipBounds);
          if (clipped) {
            allPoints.push(clipped.x1, clipped.y1, 0, clipped.x2, clipped.y2, 0);
          }
        }
      }
    }
  });
  if (allPoints.length > 0) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(allPoints, 3));
    const segments = new THREE.LineSegments(geometry, material);
    segments.userData.isPattern = true;
    if (layerType) {
      segments.userData.layerType = layerType;
    }
    scene.add(segments);
  }
}

// src/pattern-utils.ts
function calculatePatternBoundingBox(pattern, options = {}) {
  const { patternScale = 1, rotation = 0, maxDist = 500, numLines = 20 } = options;
  const drawStep = 2;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  pattern.lines.forEach((lineData) => {
    const angleRad = (lineData.angle + rotation) * Math.PI / 180;
    const dirX = Math.cos(angleRad);
    const dirY = Math.sin(angleRad);
    const perpX = -dirY;
    const perpY = dirX;
    const lineOriginX = lineData.xOrigin * patternScale;
    const lineOriginY = lineData.yOrigin * patternScale;
    const deltaX = lineData.xOffset * patternScale;
    const deltaY = lineData.yOffset * patternScale;
    let perpDist = deltaX * perpX + deltaY * perpY;
    let actualPerpDist = Math.abs(perpDist) < 1e-3 ? Math.sqrt(deltaX ** 2 + deltaY ** 2) : Math.abs(perpDist);
    if (actualPerpDist === 0) actualPerpDist = 10 * patternScale;
    const patternLength = lineData.dashes.reduce((sum, d) => sum + Math.abs(d), 0) * patternScale;
    for (let lineNum = -numLines; lineNum <= numLines; lineNum++) {
      const parallelOriginX = lineOriginX + perpX * actualPerpDist * lineNum;
      const parallelOriginY = lineOriginY + perpY * actualPerpDist * lineNum;
      const dashOffset = (deltaX * dirX + deltaY * dirY) * lineNum;
      const adjustedMaxDist = maxDist + Math.abs(dashOffset);
      for (let dist = -adjustedMaxDist; dist < adjustedMaxDist; dist += drawStep) {
        let isInDrawPhase = true;
        if (patternLength > 0) {
          const relativeDist = dist - dashOffset;
          const patternPos = (relativeDist % patternLength + patternLength) % patternLength;
          let accumulated = 0;
          for (const dash of lineData.dashes) {
            const dashLen = Math.abs(dash) * patternScale;
            accumulated += dashLen;
            if (patternPos < accumulated) {
              isInDrawPhase = dash > 0;
              break;
            }
          }
        }
        if (isInDrawPhase && Math.abs(dist) < maxDist) {
          const x1 = parallelOriginX + dirX * dist;
          const y1 = parallelOriginY + dirY * dist;
          const x2 = parallelOriginX + dirX * (dist + drawStep);
          const y2 = parallelOriginY + dirY * (dist + drawStep);
          minX = Math.min(minX, x1, x2);
          maxX = Math.max(maxX, x1, x2);
          minY = Math.min(minY, y1, y2);
          maxY = Math.max(maxY, y1, y2);
        }
      }
    }
  });
  if (minX === Infinity) {
    return { minX: -100, maxX: 100, minY: -100, maxY: 100, width: 200, height: 200, centerX: 0, centerY: 0 };
  }
  return {
    minX,
    maxX,
    minY,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2
  };
}
function calculateOptimalScale(pattern, targetWidth, targetHeight, options = {}) {
  const { rotation = 0, margin = 0.05 } = options;
  if (!pattern.lines || pattern.lines.length === 0) {
    return 1;
  }
  let totalOffset = 0;
  pattern.lines.forEach((line) => {
    const offsetMag = Math.sqrt(line.xOffset ** 2 + line.yOffset ** 2);
    totalOffset += offsetMag;
  });
  const avgOffset = totalOffset / pattern.lines.length;
  const diagonalDist = Math.sqrt(targetWidth * targetWidth + targetHeight * targetHeight);
  let initialScale = 1;
  if (avgOffset > 0) {
    initialScale = diagonalDist / (avgOffset * 0.5);
  }
  const bbox = calculatePatternBoundingBox(pattern, {
    patternScale: initialScale,
    rotation,
    maxDist: Math.max(targetWidth, targetHeight) * 2,
    numLines: 30
  });
  if (bbox.width > 0 && bbox.height > 0) {
    const fillRatioX = targetWidth * (1 - margin * 2) / bbox.width;
    const fillRatioY = targetHeight * (1 - margin * 2) / bbox.height;
    const fillRatio = Math.min(fillRatioX, fillRatioY);
    initialScale = initialScale * fillRatio;
  }
  const finalScale = initialScale * 1.2;
  return Math.max(0.1, Math.min(20, finalScale));
}

// src/pattern-preview.ts
import * as THREE2 from "three";
function createPatternPreviewScene(pattern, options = {}) {
  const {
    width = 160,
    height = 160,
    patternScale = 1,
    rotation = 0,
    backgroundColor = 16119285,
    color = 0
  } = options;
  const scene = new THREE2.Scene();
  scene.background = new THREE2.Color(backgroundColor);
  const patternSize = 600;
  const clipBounds = 300;
  const aspect = width / height;
  const containerMin = Math.min(width, height);
  const zoom = containerMin / patternSize;
  const camera = new THREE2.OrthographicCamera(
    -clipBounds / zoom * aspect,
    clipBounds / zoom * aspect,
    clipBounds / zoom,
    -clipBounds / zoom,
    0.1,
    2e3
  );
  camera.position.set(0, 0, 100);
  camera.lookAt(0, 0, 0);
  const renderer = new THREE2.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
  renderer.setSize(width, height);
  renderer.setPixelRatio(typeof window !== "undefined" ? window.devicePixelRatio : 1);
  renderPatternToSceneOptimized(scene, pattern, { patternScale, rotation, color, clipBounds });
  renderer.render(scene, camera);
  return { scene, camera, renderer };
}
function getPatternPreviewDataURL(pattern, options = {}) {
  const { renderer } = createPatternPreviewScene(pattern, options);
  const dataURL = renderer.domElement.toDataURL("image/png");
  renderer.dispose();
  return dataURL;
}

// src/composition-renderer.ts
import * as THREE3 from "three";

// src/constants.ts
var MEMBRANE_FIXED_HEIGHT = 5;
var MEMBRANE_PATTERN = {
  name: "MEMBRANE",
  description: "Special membrane pattern",
  lines: [
    { angle: 0, xOrigin: 0, yOrigin: 250, xOffset: 0, yOffset: 600, dashes: [1e3] },
    { angle: 0, xOrigin: 0, yOrigin: -250, xOffset: 0, yOffset: 600, dashes: [1e3] },
    { angle: 0, xOrigin: 0, yOrigin: 0, xOffset: 0, yOffset: 600, dashes: [30, -20] }
  ]
};
var SOLID_PATTERN_ID = "__solid__";
var SOLID_PATTERN_DATA = Object.freeze({
  name: "SOLID",
  description: "Solid fill",
  lines: []
});
function isSolidPatternData(pattern) {
  return pattern === SOLID_PATTERN_DATA || !!pattern && pattern.lines.length === 0;
}

// src/composition-renderer.ts
function drawMembraneDashes(group, layerWidth, layerHeight, color = "#000000") {
  const dashWidth = layerWidth * 0.06;
  const gapWidth = layerWidth * 0.04;
  const step = dashWidth + gapWidth;
  const colorValue = new THREE3.Color(color);
  const material = new THREE3.MeshBasicMaterial({ color: colorValue, side: THREE3.DoubleSide });
  const halfW = layerWidth / 2;
  const halfH = layerHeight / 2;
  let x = -halfW;
  while (x + dashWidth <= halfW + 0.1) {
    const shape = new THREE3.Shape();
    shape.moveTo(x, -halfH);
    shape.lineTo(x + dashWidth, -halfH);
    shape.lineTo(x + dashWidth, halfH);
    shape.lineTo(x, halfH);
    shape.lineTo(x, -halfH);
    const geo = new THREE3.ShapeGeometry(shape);
    const mesh = new THREE3.Mesh(geo, material);
    group.add(mesh);
    x += step;
  }
}
function createLayerLabel(num) {
  const size = 64;
  const canvas = typeof document !== "undefined" ? document.createElement("canvas") : null;
  if (!canvas) {
    const material2 = new THREE3.SpriteMaterial();
    const sprite2 = new THREE3.Sprite(material2);
    sprite2.scale.set(22, 22, 1);
    return sprite2;
  }
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 4, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 4;
    ctx.stroke();
    ctx.fillStyle = "#000000";
    ctx.font = "bold 32px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(num.toString(), size / 2, size / 2 + 2);
  }
  const texture = new THREE3.CanvasTexture(canvas);
  const material = new THREE3.SpriteMaterial({ map: texture });
  const sprite = new THREE3.Sprite(material);
  sprite.scale.set(22, 22, 1);
  return sprite;
}
function buildCompositionScene(layers, options = {}) {
  const { width = 350, height = 350, padding = 15 } = options;
  const scene = new THREE3.Scene();
  scene.background = new THREE3.Color(16777215);
  const camera = new THREE3.OrthographicCamera(
    -width / 2,
    width / 2,
    height / 2,
    -height / 2,
    0.1,
    1e3
  );
  camera.position.z = 10;
  if (layers.length === 0) {
    return { scene, camera };
  }
  const sortedLayers = [...layers].sort((a, b) => a.order - b.order);
  const AVAILABLE_H = height - padding * 2;
  const AVAILABLE_W = width - padding * 2;
  const PATTERN_BASE_SIZE = 600;
  const membraneCount = sortedLayers.filter((l) => l.function === "membrane_layer").length;
  const membraneTotal = membraneCount * MEMBRANE_FIXED_HEIGHT;
  const remainingHeight = Math.max(AVAILABLE_H - membraneTotal, 0);
  const nonMembraneThickness = sortedLayers.reduce(
    (sum, l) => sum + (l.function === "membrane_layer" ? 0 : Math.max(l.thickness, 0)),
    0
  );
  let currentY = 0;
  const layerDataList = sortedLayers.map((layer) => {
    const isMembrane = layer.function === "membrane_layer";
    let relativeThickness;
    if (isMembrane) {
      relativeThickness = AVAILABLE_H > 0 ? MEMBRANE_FIXED_HEIGHT / AVAILABLE_H : 0;
    } else {
      const proportion = nonMembraneThickness > 0 ? Math.max(layer.thickness, 0) / nonMembraneThickness : 0;
      relativeThickness = AVAILABLE_H > 0 ? proportion * remainingHeight / AVAILABLE_H : 0;
    }
    const yPos = currentY;
    currentY += relativeThickness;
    return { layer, relativeThickness, yPosition: yPos };
  });
  const variableLayerIndex = layerDataList.findIndex((ld) => ld.layer.is_variable);
  let rotationAngle = 0;
  if (variableLayerIndex !== -1) {
    const variableLayerData = layerDataList[variableLayerIndex];
    const variablePixelHeight = variableLayerData.relativeThickness * AVAILABLE_H;
    const dy = variablePixelHeight / 2;
    rotationAngle = Math.atan(-dy / AVAILABLE_W);
  }
  const LABEL_SIZE = 22;
  const LABEL_X_STEP = 26;
  const precomputedYCenters = layerDataList.map((ld) => {
    const pixelHeight = ld.relativeThickness * AVAILABLE_H;
    const yBottom = -AVAILABLE_H / 2 + ld.yPosition * AVAILABLE_H;
    return yBottom + pixelHeight / 2;
  });
  const labelXLevels = new Array(layerDataList.length).fill(0);
  const lastYPerLevel = [];
  layerDataList.forEach((_, index) => {
    const yCenter = precomputedYCenters[index];
    let level = 0;
    while (lastYPerLevel[level] !== void 0 && Math.abs(yCenter - lastYPerLevel[level]) < LABEL_SIZE) {
      level++;
    }
    labelXLevels[index] = level;
    lastYPerLevel[level] = yCenter;
  });
  const maxLabelLevel = Math.max(...labelXLevels);
  let previousTopLeft = null;
  let previousTopRight = null;
  layerDataList.forEach((layerData, index) => {
    const pixelHeight = layerData.relativeThickness * AVAILABLE_H;
    const yBottom = -AVAILABLE_H / 2 + layerData.yPosition * AVAILABLE_H;
    const yCenter = yBottom + pixelHeight / 2;
    const layerGroup = new THREE3.Group();
    const isVariable = layerData.layer.is_variable;
    if (variableLayerIndex !== -1 && index > variableLayerIndex) {
      let bottomLeft;
      let bottomRight;
      if (previousTopLeft && previousTopRight) {
        bottomLeft = previousTopLeft.clone();
        bottomRight = previousTopRight.clone();
      } else {
        const varLD = layerDataList[variableLayerIndex];
        const varPH = varLD.relativeThickness * AVAILABLE_H;
        const varYB = -AVAILABLE_H / 2 + varLD.yPosition * AVAILABLE_H;
        bottomLeft = new THREE3.Vector3(-AVAILABLE_W / 2, varYB + varPH, 0);
        bottomRight = new THREE3.Vector3(AVAILABLE_W / 2, varYB + varPH / 2, 0);
      }
      const topLeft = new THREE3.Vector3(-AVAILABLE_W / 2, bottomLeft.y + pixelHeight, 0);
      const topRight = new THREE3.Vector3(AVAILABLE_W / 2, bottomRight.y + pixelHeight, 0);
      const centerX = 0;
      const cY = (bottomLeft.y + bottomRight.y + topLeft.y + topRight.y) / 4;
      layerGroup.position.set(centerX, cY, 0);
      previousTopLeft = topLeft.clone();
      previousTopRight = topRight.clone();
      const blRel = new THREE3.Vector3(bottomLeft.x - centerX, bottomLeft.y - cY, 0);
      const brRel = new THREE3.Vector3(bottomRight.x - centerX, bottomRight.y - cY, 0);
      const trRel = new THREE3.Vector3(topRight.x - centerX, topRight.y - cY, 0);
      const tlRel = new THREE3.Vector3(topLeft.x - centerX, topLeft.y - cY, 0);
      const maskShape2 = new THREE3.Shape();
      maskShape2.moveTo(blRel.x, blRel.y);
      maskShape2.lineTo(brRel.x, brRel.y);
      maskShape2.lineTo(trRel.x, trRel.y);
      maskShape2.lineTo(tlRel.x, tlRel.y);
      maskShape2.lineTo(blRel.x, blRel.y);
      const maskGeometry = new THREE3.ShapeGeometry(maskShape2);
      const stencilRefVal2 = index + 1;
      const maskMesh = new THREE3.Mesh(
        maskGeometry,
        new THREE3.MeshBasicMaterial({
          colorWrite: false,
          depthWrite: false,
          side: THREE3.DoubleSide,
          stencilWrite: true,
          stencilRef: stencilRefVal2,
          stencilFunc: THREE3.AlwaysStencilFunc,
          stencilFail: THREE3.ReplaceStencilOp,
          stencilZFail: THREE3.ReplaceStencilOp,
          stencilZPass: THREE3.ReplaceStencilOp
        })
      );
      maskMesh.renderOrder = -1;
      layerGroup.add(maskMesh);
      layerGroup.userData.slopedShape = { blRel, brRel, trRel, tlRel };
      layerGroup.userData.stencilRef = stencilRefVal2;
    } else {
      layerGroup.position.y = yCenter;
      if (isVariable) {
        previousTopLeft = new THREE3.Vector3(-AVAILABLE_W / 2, yBottom + pixelHeight, 0);
        previousTopRight = new THREE3.Vector3(AVAILABLE_W / 2, yBottom + pixelHeight / 2, 0);
      } else if (variableLayerIndex === -1 || index < variableLayerIndex) {
        previousTopLeft = new THREE3.Vector3(-AVAILABLE_W / 2, yBottom + pixelHeight, 0);
        previousTopRight = new THREE3.Vector3(AVAILABLE_W / 2, yBottom + pixelHeight, 0);
      }
    }
    const isRotatedLayer = variableLayerIndex !== -1 && index > variableLayerIndex;
    const uniformScale = AVAILABLE_W / PATTERN_BASE_SIZE;
    const patternSpaceHalfHeight = pixelHeight / 2 / uniformScale;
    const normalClipBounds = PATTERN_BASE_SIZE / 2;
    const clipBoundsForDraw = isRotatedLayer ? Math.max(PATTERN_BASE_SIZE, patternSpaceHalfHeight) : Math.max(normalClipBounds, patternSpaceHalfHeight);
    const patternSubGroup = isRotatedLayer ? new THREE3.Group() : null;
    if (patternSubGroup) {
      patternSubGroup.rotation.z = rotationAngle;
      layerGroup.add(patternSubGroup);
    }
    const patternTarget = patternSubGroup || layerGroup;
    const tempScene = new THREE3.Scene();
    const draw = (pattern, color, type, patternScale = 1) => {
      renderPatternToSceneOptimized(tempScene, pattern, {
        patternScale,
        color,
        layerType: type,
        clipBounds: clipBoundsForDraw
      });
      [...tempScene.children].forEach((child) => {
        if (child.userData.layerType === type) {
          const cloned = child.clone();
          cloned.scale.set(uniformScale, uniformScale, 1);
          patternTarget.add(cloned);
        }
      });
      tempScene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) {
            obj.material.forEach((m) => m.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
      tempScene.clear();
    };
    const stencilRefVal = index + 1;
    const maskShape = new THREE3.Shape();
    if (isVariable) {
      maskShape.moveTo(-AVAILABLE_W / 2, -pixelHeight / 2);
      maskShape.lineTo(AVAILABLE_W / 2, -pixelHeight / 2);
      maskShape.lineTo(AVAILABLE_W / 2, 0);
      maskShape.lineTo(-AVAILABLE_W / 2, pixelHeight / 2);
      maskShape.lineTo(-AVAILABLE_W / 2, -pixelHeight / 2);
    } else if (isRotatedLayer && layerGroup.userData.slopedShape) {
    } else {
      maskShape.moveTo(-AVAILABLE_W / 2, -pixelHeight / 2);
      maskShape.lineTo(AVAILABLE_W / 2, -pixelHeight / 2);
      maskShape.lineTo(AVAILABLE_W / 2, pixelHeight / 2);
      maskShape.lineTo(-AVAILABLE_W / 2, pixelHeight / 2);
      maskShape.lineTo(-AVAILABLE_W / 2, -pixelHeight / 2);
    }
    if (!isRotatedLayer) {
      const maskGeometry = new THREE3.ShapeGeometry(maskShape);
      const maskMesh = new THREE3.Mesh(
        maskGeometry,
        new THREE3.MeshBasicMaterial({
          colorWrite: false,
          depthWrite: false,
          side: THREE3.DoubleSide,
          stencilWrite: true,
          stencilRef: stencilRefVal,
          stencilFunc: THREE3.AlwaysStencilFunc,
          stencilFail: THREE3.ReplaceStencilOp,
          stencilZFail: THREE3.ReplaceStencilOp,
          stencilZPass: THREE3.ReplaceStencilOp
        })
      );
      maskMesh.renderOrder = -1;
      layerGroup.add(maskMesh);
      layerGroup.userData.stencilRef = stencilRefVal;
    }
    const isMembrane = layerData.layer.function === "membrane_layer";
    if (isMembrane) {
      drawMembraneDashes(patternTarget, AVAILABLE_W, pixelHeight, "#000000");
    } else {
      if (layerData.layer.bgPattern) {
        const bgColor = layerData.layer.bg_color_hex || "#ffffff";
        if (layerData.layer.bgPattern.lines.length === 0) {
          const geo = new THREE3.PlaneGeometry(AVAILABLE_W, pixelHeight);
          const mat = new THREE3.MeshBasicMaterial({ color: bgColor });
          const mesh = new THREE3.Mesh(geo, mat);
          mesh.position.z = -0.1;
          mesh.userData = { isSolidFill: true };
          patternTarget.add(mesh);
        } else {
          const bgScale = layerData.layer.bg_scale ?? 1;
          draw(layerData.layer.bgPattern, bgColor, `bg-${index}`, bgScale);
        }
      }
      if (layerData.layer.fgPattern) {
        const fgColor = layerData.layer.fg_color_hex || "#000000";
        if (layerData.layer.fgPattern.lines.length === 0) {
          const geo = new THREE3.PlaneGeometry(AVAILABLE_W, pixelHeight);
          const mat = new THREE3.MeshBasicMaterial({ color: fgColor });
          const mesh = new THREE3.Mesh(geo, mat);
          mesh.position.z = 0;
          mesh.userData = { isSolidFill: true };
          patternTarget.add(mesh);
        } else {
          const fgScale = layerData.layer.fg_scale ?? 1;
          draw(layerData.layer.fgPattern, fgColor, `fg-${index}`, fgScale);
        }
      }
    }
    if (layerGroup.userData.stencilRef) {
      const refVal = layerGroup.userData.stencilRef;
      layerGroup.traverse((child) => {
        if ((child instanceof THREE3.Line || child instanceof THREE3.LineSegments) && child.userData.layerType) {
          const mat = child.material;
          const newMat = mat.clone();
          newMat.stencilWrite = true;
          newMat.stencilRef = refVal;
          newMat.stencilFunc = THREE3.EqualStencilFunc;
          newMat.stencilFail = THREE3.KeepStencilOp;
          newMat.stencilZFail = THREE3.KeepStencilOp;
          newMat.stencilZPass = THREE3.KeepStencilOp;
          child.material = newMat;
        }
        if (child instanceof THREE3.Mesh && child.userData.isSolidFill) {
          const mat = child.material;
          const newMat = mat.clone();
          newMat.stencilWrite = true;
          newMat.stencilRef = refVal;
          newMat.stencilFunc = THREE3.EqualStencilFunc;
          newMat.stencilFail = THREE3.KeepStencilOp;
          newMat.stencilZFail = THREE3.KeepStencilOp;
          newMat.stencilZPass = THREE3.KeepStencilOp;
          child.material = newMat;
        }
      });
    }
    let borderPoints;
    if (isVariable) {
      borderPoints = [
        new THREE3.Vector3(-AVAILABLE_W / 2, -pixelHeight / 2, 0),
        new THREE3.Vector3(AVAILABLE_W / 2, -pixelHeight / 2, 0),
        new THREE3.Vector3(AVAILABLE_W / 2, 0, 0),
        new THREE3.Vector3(-AVAILABLE_W / 2, pixelHeight / 2, 0),
        new THREE3.Vector3(-AVAILABLE_W / 2, -pixelHeight / 2, 0)
      ];
    } else if (isRotatedLayer && layerGroup.userData.slopedShape) {
      const s = layerGroup.userData.slopedShape;
      borderPoints = [
        new THREE3.Vector3(s.blRel.x, s.blRel.y, 0),
        new THREE3.Vector3(s.brRel.x, s.brRel.y, 0),
        new THREE3.Vector3(s.trRel.x, s.trRel.y, 0),
        new THREE3.Vector3(s.tlRel.x, s.tlRel.y, 0),
        new THREE3.Vector3(s.blRel.x, s.blRel.y, 0)
      ];
    } else {
      const bw = AVAILABLE_W / 2;
      borderPoints = [
        new THREE3.Vector3(-bw, -pixelHeight / 2, 0),
        new THREE3.Vector3(bw, -pixelHeight / 2, 0),
        new THREE3.Vector3(bw, pixelHeight / 2, 0),
        new THREE3.Vector3(-bw, pixelHeight / 2, 0),
        new THREE3.Vector3(-bw, -pixelHeight / 2, 0)
      ];
    }
    const borderGeo = new THREE3.BufferGeometry().setFromPoints(borderPoints);
    const borderMat = new THREE3.LineBasicMaterial({ color: 3355443 });
    layerGroup.add(new THREE3.Line(borderGeo, borderMat));
    const label = createLayerLabel(index + 1);
    label.position.set(AVAILABLE_W / 2 + 25 + labelXLevels[index] * LABEL_X_STEP, 0, 0);
    layerGroup.add(label);
    scene.add(layerGroup);
  });
  const compositionMinY = -AVAILABLE_H / 2;
  const finalTopLeft = previousTopLeft;
  const finalTopRight = previousTopRight;
  const compositionMaxY = finalTopLeft ? Math.max(finalTopLeft.y, finalTopRight ? finalTopRight.y : finalTopLeft.y) : AVAILABLE_H / 2;
  const compositionCenterX = 0;
  const compositionCenterY = (compositionMinY + compositionMaxY) / 2;
  const compositionW = AVAILABLE_W + 50 + maxLabelLevel * LABEL_X_STEP;
  const compositionH = compositionMaxY - compositionMinY;
  const scenePadding = 60;
  const aspect = width / height;
  const viewW = compositionW + scenePadding;
  const viewH = compositionH + scenePadding;
  if (viewW / viewH > aspect) {
    const horizontalSize = viewW;
    const verticalSize = viewW / aspect;
    camera.left = -horizontalSize / 2;
    camera.right = horizontalSize / 2;
    camera.top = verticalSize / 2;
    camera.bottom = -verticalSize / 2;
  } else {
    const verticalSize = viewH;
    const horizontalSize = viewH * aspect;
    camera.top = verticalSize / 2;
    camera.bottom = -verticalSize / 2;
    camera.left = -horizontalSize / 2;
    camera.right = horizontalSize / 2;
  }
  camera.position.set(compositionCenterX, compositionCenterY, 10);
  camera.updateProjectionMatrix();
  return { scene, camera };
}

// src/geometry-renderer.ts
import * as THREE4 from "three";
var prepareGeometry = (geometry) => {
  const parsedGeometry = JSON.parse(geometry);
  if (!parsedGeometry.Solids) {
    return;
  }
  return {
    Triangles: parsedGeometry.Solids.TrianglesNum,
    Vertices: parsedGeometry.Solids.VerticesNum,
    VertCoordinates: parsedGeometry.Solids.Vertices,
    TriangleIndices: parsedGeometry.Solids.TriangleIndicies,
    Curves: parsedGeometry.Curves
  };
};
var computeGeometry = (nTriang, nVertices, VertCoord, TriangIndices) => {
  const group = new THREE4.Group();
  const material = new THREE4.MeshBasicMaterial({ color: 16777215 });
  for (const i in nTriang) {
    const SplitVertCoordtmp = VertCoord[i].split(";");
    const SplitnTriang = nTriang[i].split(";");
    const SplitTriangIndices = TriangIndices[i].split(";");
    const SplitTnVertices = nVertices[i].split(";");
    const SplitVertCoord = SplitVertCoordtmp.map((element) => {
      return Number(element.replace(",", ".")) * 304.8;
    });
    let x = 0;
    let y = 0;
    for (const l in SplitTnVertices) {
      const TempGeometry = new THREE4.BufferGeometry();
      const verticesArray = [];
      const indicesArray = [];
      for (let k = x; k < x + SplitTnVertices[l] * 3; k += 3) {
        verticesArray.push(SplitVertCoord[k], SplitVertCoord[k + 1], SplitVertCoord[k + 2]);
      }
      for (let j = y; j < y + SplitnTriang[l] * 3; j += 3) {
        indicesArray.push(
          SplitTriangIndices[j],
          SplitTriangIndices[j + 1],
          SplitTriangIndices[j + 2]
        );
      }
      const vertices = new Float32Array(verticesArray);
      const indices = new Uint32Array(indicesArray);
      TempGeometry.setAttribute("position", new THREE4.BufferAttribute(vertices, 3));
      TempGeometry.setIndex(new THREE4.BufferAttribute(indices, 1));
      const TempMesh = new THREE4.Mesh(TempGeometry, material);
      TempMesh.castShadow = true;
      TempMesh.receiveShadow = true;
      group.add(TempMesh);
      const edgesGeometry = new THREE4.EdgesGeometry(TempGeometry);
      const line = new THREE4.LineSegments(
        edgesGeometry,
        new THREE4.LineBasicMaterial({ color: 0 })
      );
      group.add(line);
      x += SplitTnVertices[l] * 3;
      y += SplitnTriang[l] * 3;
    }
  }
  return { group };
};
var renderCurves = (group, Curves, layers) => {
  Curves.forEach((e) => {
    if (!layers.find((layer) => layer.name === e.layer)?.visible) {
      return;
    }
    const points = e.points.map((point) => new THREE4.Vector3(point.X, point.Y, point.Z));
    const lineDistances = [];
    let d = 0;
    for (let i = 0; i < points.length; i++) {
      if (i > 0) {
        d += points[i].distanceTo(points[i - 1]);
      }
      lineDistances[i] = d;
    }
    const lineGeom = new THREE4.BufferGeometry().setFromPoints(points);
    lineGeom.setAttribute(
      "lineDistance",
      new THREE4.BufferAttribute(new Float32Array(lineDistances), 1)
    );
    let linematerial = new THREE4.LineBasicMaterial({
      color: 255,
      linewidth: 1,
      linecap: "round",
      linejoin: "round"
    });
    if (e.lineStyle != null) {
      const json = JSON.parse(e.lineStyle);
      const segments = json != null ? json.Pattern : [];
      let totalSize = 0;
      let conds = "";
      const scale = 5e3;
      try {
        JSON.parse(segments).forEach((f) => {
          if (f["Type"] == "Dash") {
            conds += `
              if(distance > ${(totalSize * scale).toFixed(1)} && distance < ${((totalSize + f["Lenght"]) * scale).toFixed(1)}){
                return false;
              }`;
            totalSize += f["Lenght"];
          }
          if (f["Type"] == "Space") {
            conds += `
              if(distance > ${(totalSize * scale).toFixed(1)} && distance < ${((totalSize + f["Lenght"]) * scale).toFixed(1)}){
                return true;
              }`;
            totalSize += f["Lenght"];
          }
          if (f["Type"] == "Dot") {
            conds += `
              if(distance > ${(totalSize * scale).toFixed(1)} && distance < ${((totalSize + 0.1) * scale).toFixed(1)}){
                return false;
              }`;
            totalSize += 0.1;
          }
        });
      } catch (e2) {
        console.log(e2);
      }
      const lineFragShader = `
        uniform vec3 diffuse;
        uniform float opacity;
        uniform float dotSize;
        float gapSize;
        float dashSize;
        float totalSize;
        varying float vLineDistance;

        bool checkParameter(float distance){
          ${conds}
          return true;
        }

        void main() {
          totalSize = ${(totalSize * scale).toFixed(1)};
          float modulo = mod( vLineDistance, totalSize );

          if (checkParameter(modulo)) {
            discard;
          }

          gl_FragColor = vec4( diffuse, opacity );
        }`;
      const lineVertShader = `
        attribute float lineDistance;
        varying float vLineDistance;

        void main() {
          vLineDistance = lineDistance;
          vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
          gl_Position = projectionMatrix * mvPosition;
        }`;
      linematerial = new THREE4.ShaderMaterial({
        uniforms: {
          diffuse: { value: new THREE4.Color("red") },
          gapSize: { value: 2 * scale },
          dotSize: { value: 0.1 * scale },
          opacity: { value: 1 }
        },
        vertexShader: lineVertShader,
        fragmentShader: lineFragShader,
        transparent: true
      });
    }
    const line = new THREE4.Line(lineGeom, linematerial);
    group.add(line);
  });
};
var renderGeometry = (geometry, layers = []) => {
  const prepared = prepareGeometry(geometry);
  if (!prepared) return new THREE4.Group();
  const { Triangles, Vertices, VertCoordinates, TriangleIndices, Curves } = prepared;
  if (Curves === null) return new THREE4.Group();
  const { group } = computeGeometry(Triangles, Vertices, VertCoordinates, TriangleIndices);
  renderCurves(group, Curves, layers);
  return group;
};
var buildGeometryScene = (options) => {
  const {
    geometry,
    layers = [],
    perspective,
    zoom,
    position
  } = options;
  const isPerspective = perspective === true;
  const cameraZoom = zoom ?? 1;
  const scene = new THREE4.Scene();
  scene.background = new THREE4.Color("rgb(230, 230, 230)");
  const camera = isPerspective ? new THREE4.PerspectiveCamera(45, 1, 1, 1e9) : new THREE4.OrthographicCamera(-1340, 1340, 1340, -1340, 0, 1e9);
  const groupOfGeometryElements = renderGeometry(geometry, layers);
  groupOfGeometryElements.rotateX(-Math.PI / 2);
  const axisCenter = new THREE4.Vector3(0, 0, 0);
  groupOfGeometryElements.position.copy(axisCenter);
  const boundingBox = new THREE4.Box3();
  boundingBox.setFromObject(groupOfGeometryElements);
  boundingBox.getCenter(axisCenter);
  groupOfGeometryElements.position.set(-axisCenter.x, -axisCenter.y, -axisCenter.z);
  const boundingBoxSize = boundingBox.getSize(new THREE4.Vector3());
  if (!isPerspective && boundingBoxSize) {
    const orthoCamera = camera;
    const cameraWidth = boundingBoxSize.x * 0.55;
    const cameraHeight = boundingBoxSize.y * 0.55;
    const cameraPosition = cameraHeight > cameraWidth ? cameraHeight : cameraWidth;
    orthoCamera.left = cameraPosition / -1;
    orthoCamera.right = cameraPosition / 1;
    orthoCamera.top = cameraPosition / 1;
    orthoCamera.bottom = cameraPosition / -1;
    orthoCamera.updateProjectionMatrix();
  }
  camera.zoom = cameraZoom;
  if (isPerspective) {
    const perspCamera = camera;
    const fov = perspCamera.fov * (Math.PI / 180);
    const distance = Math.max(
      boundingBox.max.x / (2 * Math.tan(fov / 2)),
      boundingBox.max.y / (2 * Math.tan(fov / 2))
    );
    camera.position.set(0, 0, boundingBox.max.z / 2 + distance + 100);
  } else {
    camera.position.set(0, 0, 100);
  }
  camera.lookAt(new THREE4.Vector3(0, 0, 0));
  if (position && position !== "[]") {
    try {
      const parse = JSON.parse(position);
      const mat = new THREE4.Matrix4();
      mat.fromArray(parse);
      camera.position.setFromMatrixPosition(mat);
    } catch (e) {
      console.log(e);
    }
  }
  camera.updateProjectionMatrix();
  scene.add(groupOfGeometryElements);
  return { scene, camera, boundingBoxSize };
};
export {
  MEMBRANE_FIXED_HEIGHT,
  MEMBRANE_PATTERN,
  SOLID_PATTERN_DATA,
  SOLID_PATTERN_ID,
  buildCompositionScene,
  buildGeometryScene,
  calculateOptimalScale,
  calculatePatternBoundingBox,
  clipLineSegment,
  createPatternPreviewScene,
  getPatternPreviewDataURL,
  isSolidPatternData,
  parsePATFile,
  renderGeometry,
  renderPatternToSceneOptimized
};
//# sourceMappingURL=index.mjs.map